import { Component, computed, effect, input, output, signal, untracked, ViewEncapsulation } from '@angular/core';
import { RouterLink } from '@angular/router';
import { LogoComponent } from '../../../logo/logo.component';

/** Minimal shape expected by the license badge (avoids coupling to cabinet-features). */
export interface HeaderLicenseStatus {
  state: string;
  licensed: boolean;
  trial: boolean;
  licenseType: 'node-locked' | 'floating' | null;
  trialDaysRemaining: number | null;
}

@Component({
    selector: 'app-header-cabinet',
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss',
    encapsulation: ViewEncapsulation.ShadowDom,
    imports: [LogoComponent, RouterLink],
    host: {
        role: 'banner'
    }
})
export class HeaderCabinetComponent {
  shouldShowCompanyLogo = input<boolean>(false);
  currentCompany = input<{ name: string; logo_url?: string; owner_user?: { email: string } } | null>(null);
  licenseStatus = input<HeaderLicenseStatus | null>(null);
  pluginConnected = input<boolean>(false);
  /** Where the logo link goes. */
  homeRoute = input('/user/projects');
  /** Whether the navigation drawer is open (narrow viewports). */
  navOpen = input(false);
  /** id of the rail the menu button opens. */
  navControls = input<string | null>(null);

  readonly companyLogoFailed = signal(false);

  constructor() {
    // A new logo gets a fresh chance to load.
    effect(() => {
      const logo = this.currentCompany()?.logo_url;
      untracked(() => this.companyLogoFailed.set(!logo));
    });
  }

  toggleMobileSidebar = output<void>();
  badgeClick = output<void>();

  badgeText = computed<string | null>(() => {
    const status = this.licenseStatus();
    if (!status) return null;

    switch (status.state) {
      case 'hardLocked':
        return 'Licensed';
      case 'floating':
        return 'Floating';
      case 'serverMode':
        return 'Server';
      case 'trial':
        return status.trialDaysRemaining != null
          ? `Trial: ${status.trialDaysRemaining} days left`
          : 'Trial';
      default:
        return null;
    }
  });

  badgeClass = computed<string>(() => {
    const status = this.licenseStatus();
    if (status?.licensed) return 'license-badge--active';
    if (status?.trial) return 'license-badge--trial';
    return '';
  });

  onBadgeClick(): void {
    this.badgeClick.emit();
  }
}
